import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  Share2, 
  Presentation, 
  Banknote, 
  Users, 
  Building2, 
  Rocket, 
  Phone,
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle
} from "lucide-react";
import { getErrorMessage } from "@/lib/errorUtils";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

const earningsData = [
  { team: "5 venditori", investment: "6.000€", earnings: "1.200€" },
  { team: "10 venditori", investment: "12.000€", earnings: "2.400€" },
  { team: "50 venditori", investment: "60.000€", earnings: "12.000€" },
  { team: "100 venditori", investment: "120.000€", earnings: "24.000€" }
];

const steps = [
  {
    icon: Share2,
    title: "Segnala un'azienda",
    description: "Inserisci nel form l'azienda che vuoi presentarci oppure condividi direttamente il tuo link referral."
  },
  {
    icon: Presentation,
    title: "Facciamo noi la demo",
    description: "Il team Aiclo si occuperà della presentazione del software e della trattativa."
  },
  {
    icon: Banknote,
    title: "Vieni pagato",
    description: "Se l'azienda diventa cliente Aiclo, sarai avvertito e riceverai il 20% del primo anno di abbonamento con bonifico bancario."
  }
];

const audiences = [
  { icon: Users, text: "Imprenditori" },
  { icon: Building2, text: "Direttori commerciali" },
  { icon: Phone, text: "Aziende con team vendite fisici o in remoto" },
  { icon: Rocket, text: "Startup che stanno scalando il reparto commerciale" }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, login, register } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [registrationDone, setRegistrationDone] = useState(false);
  const { forgotPassword } = useAuth();
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
    confirm_password: "",
    accept_privacy: false,
    accept_terms: false
  });
  
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [forgotEmail, setForgotEmail] = useState("");

  // Redirect if already logged in
  if (user) {
    navigate(user.is_staff ? "/staff" : "/dashboard");
  }

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirm_password) {
      toast.error("Le password non coincidono");
      return;
    }

    if (!formData.accept_privacy || !formData.accept_terms) {
      toast.error("Devi accettare il trattamento dati e i termini del programma");
      return;
    }
    
    setIsLoading(true);
    try {
      await register({
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        accept_terms: formData.accept_terms
      });
      toast.success("Registrazione completata! Controlla la tua email per verificare l'account.");
      setRegistrationDone(true);
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore durante la registrazione"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const userData = await login(loginData.email, loginData.password);
      toast.success("Login effettuato con successo!");
      setShowLogin(false);
      navigate(userData.is_staff ? "/staff" : "/dashboard");
    } catch (error) {
      toast.error(getErrorMessage(error, "Credenziali non valide"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await forgotPassword(forgotEmail);
      toast.success("Se l'email esiste, riceverai un link per reimpostare la password.");
      setShowForgotPassword(false);
      setForgotEmail("");
    } catch (error) {
      toast.error("Errore durante l'invio dell'email");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header with Logo */}
      <header className="absolute top-0 left-0 right-0 z-50 py-4 px-6">
        <div className="container mx-auto">
          <img 
            src="https://aiclo.io/wp-content/uploads/2025/09/aiclo.svg" 
            alt="Aiclo" 
            className="h-7"
          />
        </div>
      </header>

      {/* Hero Section - Compact */}
      <section className="relative min-h-[85vh] flex items-center hero-gradient grid-pattern pt-16">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-[#fc0301]/15 rounded-full blur-[150px]" />
          <div className="absolute top-[20%] right-0 w-[400px] h-[300px] bg-[#fc0301]/8 rounded-full blur-[120px]" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background pointer-events-none" />
        
        <div className="container mx-auto px-6 py-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div {...fadeInUp}>
              <span className="inline-block px-4 py-1.5 rounded-full bg-white/[0.06] border border-white/10 text-muted-foreground text-sm font-medium mb-5">
                Programma Partner Aiclo
              </span>
            </motion.div>
            
            <motion.h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-5"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Parla di Aiclo e guadagna fino a{" "}
              <span className="text-gold-shimmer">24.000€</span>{" "}
              per segnalazione
            </motion.h1>
            
            <motion.p 
              className="text-base md:text-lg text-muted-foreground mb-8 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Presenta Aiclo ad imprenditori e manager nella tua cerchia, se diventano clienti ricevi il 20% del valore del primo anno di abbonamento.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Button 
                size="lg"
                className="h-12 px-8 rounded-full btn-gold hover:scale-105 active:scale-95 transition-transform"
                onClick={() => document.getElementById('register-form').scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-register-btn"
              >
                Registrati <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="h-12 px-8 rounded-full bg-white/5 hover:bg-white/10 border-white/20"
                onClick={() => setShowLogin(true)}
                data-testid="hero-login-btn"
              >
                Login
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">Come funziona</h2>
            <p className="text-muted-foreground text-lg">Tre semplici step per iniziare a guadagnare</p>
          </motion.div>
          
          <motion.div 
            className="grid md:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {steps.map((step, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="glass-card p-8 feature-card"
              >
                <div className="w-14 h-14 rounded-xl bg-white/[0.06] flex items-center justify-center mb-6">
                  <step.icon className="w-7 h-7 text-muted-foreground" />
                </div>
                <div className="text-sm text-[#fcb900] font-medium mb-2">Step {index + 1}</div>
                <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Earnings Table */}
      <section className="py-20 px-6 bg-card/30">
        <div className="container mx-auto max-w-4xl">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">Quanto puoi guadagnare</h2>
            <p className="text-muted-foreground text-lg">Il 20% del primo anno di abbonamento</p>
          </motion.div>
          
          <motion.div 
            className="glass-card overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <table className="table-earnings" data-testid="earnings-table">
              <thead>
                <tr className="bg-white/[0.02]">
                  <th>Team vendite</th>
                  <th>Investimento annuo</th>
                  <th>Il tuo guadagno</th>
                </tr>
              </thead>
              <tbody>
                {earningsData.map((row, index) => (
                  <tr key={index} className="group cursor-default">
                    <td className="font-medium group-hover:text-foreground transition-colors">{row.team}</td>
                    <td className="text-muted-foreground group-hover:text-foreground transition-colors">{row.investment}</td>
                    <td className="text-gold-shimmer font-semibold group-hover:brightness-150 transition-all">{row.earnings}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* Who Can Participate */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">A chi puoi presentare Aiclo</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Il programma è aperto a chi conosce:
            </p>
          </motion.div>
          
          <motion.div 
            className="grid sm:grid-cols-2 gap-4"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {audiences.map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="flex items-center gap-4 glass-card p-5"
              >
                <div className="w-10 h-10 rounded-lg bg-[#fcb900]/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-[#fcb900]" />
                </div>
                <span className="text-foreground">{item.text}</span>
              </motion.div>
            ))}
          </motion.div>
          
          <motion.p 
            className="text-center text-muted-foreground mt-8 max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Se conosci aziende che hanno team commerciale, che lavorano in presenza, al telefono o in videocall, Aiclo può aiutarle a migliorare le loro performance e semplificare il coaching.
          </motion.p>
        </div>
      </section>

      {/* Registration Form */}
      <section id="register-form" className="py-20 px-6 bg-card/30">
        <div className="container mx-auto max-w-xl">
          <motion.div 
            className="text-center mb-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">Diventa partner</h2>
            <p className="text-muted-foreground">
              Compila il form per entrare nel programma referral Aiclo e generare il tuo link di segnalazione.
            </p>
          </motion.div>
          
          {registrationDone ? (
            <motion.div
              className="glass-card p-12 text-center"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              data-testid="registration-success"
            >
              <div className="w-20 h-20 mx-auto rounded-full bg-[#fcb900]/20 flex items-center justify-center mb-6">
                <CheckCircle className="w-10 h-10 text-[#fcb900]" />
              </div>
              <h3 className="text-2xl font-semibold mb-3">Registrazione completata!</h3>
              <p className="text-muted-foreground text-lg mb-6">
                Controlla la tua email per verificare l'account e attivare il tuo link referral.
              </p>
              <p className="text-sm text-muted-foreground mb-8">
                Non hai ricevuto l'email? Controlla la cartella spam oppure{" "}
                <button
                  type="button"
                  onClick={() => setRegistrationDone(false)}
                  className="text-[#fcb900] hover:underline"
                >
                  registrati di nuovo
                </button>.
              </p>
              <Button
                onClick={() => setShowLogin(true)}
                className="btn-gold rounded-full px-8 h-12"
                data-testid="go-to-login-btn"
              >
                Vai al Login
              </Button>
            </motion.div>
          ) : (
          <motion.form 
            onSubmit={handleRegister}
            className="glass-card p-8 space-y-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="register-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">Nome</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="bg-black/20 border-white/10 h-11"
                  required
                  data-testid="input-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Cognome</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="bg-black/20 border-white/10 h-11"
                  required
                  data-testid="input-last-name"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-black/20 border-white/10 h-11"
                required
                data-testid="input-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Telefono</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="bg-black/20 border-white/10 h-11"
                required
                data-testid="input-phone"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="bg-black/20 border-white/10 h-11 pr-10"
                  placeholder="Min 8 caratteri, 1 maiuscola, 1 speciale"
                  required
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Conferma Password</Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                  className="bg-black/20 border-white/10 h-11 pr-10"
                  required
                  data-testid="input-confirm-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <Checkbox
                id="accept_privacy"
                checked={formData.accept_privacy}
                onCheckedChange={(checked) => setFormData({ ...formData, accept_privacy: checked })}
                data-testid="checkbox-privacy"
              />
              <Label htmlFor="accept_privacy" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                Dichiaro di aver letto l'<a href="https://aiclo.io/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-[#fcb900] hover:underline">Informativa sulla Privacy</a> e acconsento al trattamento dei miei dati personali ai sensi del Regolamento UE 2016/679 (GDPR).
              </Label>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                id="accept_terms"
                checked={formData.accept_terms}
                onCheckedChange={(checked) => setFormData({ ...formData, accept_terms: checked })}
                data-testid="checkbox-terms"
              />
              <Label htmlFor="accept_terms" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                Accetto i{" "}
                <a href="https://aiclo.io/termini-e-condizioni-programma-referral-aiclo/" target="_blank" rel="noopener noreferrer" className="text-[#fcb900] hover:underline">
                  termini e condizioni del programma referral Aiclo
                </a>
              </Label>
            </div>
            
            <Button 
              type="submit" 
              className="w-full h-12 rounded-full btn-gold"
              disabled={isLoading}
              data-testid="submit-register-btn"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Invia
            </Button>
            
            <p className="text-center text-sm text-muted-foreground">
              Hai già un account?{" "}
              <button
                type="button"
                onClick={() => setShowLogin(true)}
                className="text-[#fcb900] hover:underline"
                data-testid="show-login-btn"
              >
                Accedi
              </button>
            </p>
          </motion.form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5">
        <div className="container mx-auto text-center text-sm text-muted-foreground">
          © {new Date().getFullYear()} Aiclo - Conversation Intelligence. Tutti i diritti riservati.
        </div>
      </footer>

      {/* Login Modal */}
      <Dialog open={showLogin} onOpenChange={setShowLogin}>
        <DialogContent className="sm:max-w-md bg-card border-white/10" data-testid="login-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">Accedi al tuo account</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLogin} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="login_email">Email</Label>
              <Input
                id="login_email"
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                className="bg-black/20 border-white/10 h-11"
                required
                data-testid="login-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="login_password">Password</Label>
              <Input
                id="login_password"
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                className="bg-black/20 border-white/10 h-11"
                required
                data-testid="login-password"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full h-11 rounded-full btn-gold"
              disabled={isLoading}
              data-testid="submit-login-btn"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Accedi
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              <button
                type="button"
                onClick={() => {
                  setShowLogin(false);
                  setShowForgotPassword(true);
                }}
                className="text-[#fcb900] hover:underline"
                data-testid="forgot-password-btn"
              >
                Password dimenticata?
              </button>
            </p>
          </form>
        </DialogContent>
      </Dialog>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md bg-card border-white/10" data-testid="forgot-password-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">Recupera password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Inserisci la tua email e ti invieremo un link per reimpostare la password.
            </p>
            <div className="space-y-2">
              <Label htmlFor="forgot_email">Email</Label>
              <Input
                id="forgot_email"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="bg-black/20 border-white/10 h-11"
                required
                data-testid="forgot-email-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full h-11 rounded-full btn-gold"
              disabled={isLoading}
              data-testid="submit-forgot-btn"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Invia link di recupero
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
